// SEQ-169 hire comments: context so a hire register row can render its audit
// notes badge without HireRegisterSection growing props for flags, unread counts
// and an open handler, and without the register having to select audit_flag.
//
// ProjectDetail owns the data and wraps the register in HireCommentsProvider.
// The register renders HireCommentsBadge beside each item. Outside a provider
// the badge renders nothing, so the register stays usable on its own.

import { createContext, useContext, type CSSProperties, type ReactNode } from "react";
import { EntityCommentsBadge } from "./EntityCommentsBadge";
import type { CommentFlag } from "../lib/entityComments";

interface HireCommentsContextValue {
  /** Needs-review flag per hire register id, from claims_hire_register.audit_flag. */
  flags: Record<string, CommentFlag>;
  /** Unread audit notes per hire register id for the current viewer. */
  unreadCounts: Record<string, number>;
  /** Opens the audit notes modal for one hire item. */
  onOpen: (hireItemId: string, label: string) => void;
}

const HireCommentsContext = createContext<HireCommentsContextValue | null>(null);

export function HireCommentsProvider({
  flags,
  unreadCounts,
  onOpen,
  children,
}: HireCommentsContextValue & { children: ReactNode }) {
  return (
    <HireCommentsContext.Provider value={{ flags, unreadCounts, onOpen }}>
      {children}
    </HireCommentsContext.Provider>
  );
}

/**
 * Audit notes dot and unread bubble for one hire register row.
 *
 * Mount it inside the row card but outside the expand button, because it
 * contains a button of its own. It stops its own clicks, so the row underneath
 * does not expand when the badge is used.
 */
export function HireCommentsBadge({
  hireItemId,
  label,
  style,
}: {
  hireItemId: string;
  /** Shown in the modal header, normally the hire item name. */
  label: string;
  style?: CSSProperties;
}) {
  const context = useContext(HireCommentsContext);
  if (!context) return null;

  return (
    <EntityCommentsBadge
      flag={context.flags[hireItemId]}
      unreadCount={context.unreadCounts[hireItemId] || 0}
      onOpen={() => context.onOpen(hireItemId, label)}
      style={style}
    />
  );
}
